import React from 'react';
import { SeeIt } from '../components/SeeIt';
import { Faq } from '../components/Faq';
import { CtaBand } from '../components/CtaBand';
import { Ledger } from '../components/Ledger';
import { ChunkVsAtom } from '../components/how/ChunkVsAtom';
import { ConstitutionalFlow } from '../components/how/ConstitutionalFlow';
import { ReceiptCard } from '../components/how/ReceiptCard';
import { AgentChain } from '../components/how/AgentChain';
import { faqsHow } from '../data/faqsHow';
import { DEMO_URL } from '../data/links';
const ENGINES = [{
  stage: 'Harvest',
  n: '01 · Atomizer',
  title: 'Atomizer.',
  body: <>
        The written rules — FDA, HIPAA, FINRA, your brand book — and the moments nobody writes down become binary
        DO/DON'T atoms: owned, versioned, ready to fire.
      </>
}, {
  stage: 'Seat',
  n: '02 · Composer',
  title: 'Composer.',
  body: <>
        Fire the musts by trigger — off the top of the budget. Then hand retrieval a governed vocabulary, so RAG
        searches for what the rules mean, not what the prompt resembles.
      </>
}, {
  stage: 'Ship',
  n: '03 · Compiler',
  title: 'Compiler.',
  body: <>
        Assemble the payload in the model's own language, and sign the receipt: which obligations fired, which
        versions, on what trigger.
      </>
}];
export function How() {
  return <>
      <header className="hero" data-id="element-464" data-mp="2p81u19fmsoee">
        <div className="hgrid wide-r" data-id="element-465" data-mp="2p936nicapiy8">
          <div className="col-l" data-id="element-466" data-mp="2u63cpt95alpc">
            <div className="lab-row" data-id="element-467" data-mp="2u5ac7a0s4sfn">
              <span className="clabel" data-id="element-468" data-mp="2fgxjqn0whrpw">How it works</span>
            </div>
            <div className="head-block" data-id="element-469" data-mp="2feurdwc9zmi0">
              <h1 style={{
              fontSize: 'clamp(28px,3.3vw,40px)',
              maxWidth: 'none'
            }} data-id="element-470" data-mp="2kfifa957jn80">
                From a simple request
                <br data-id="element-471" data-mp="384ta79zvmne8" />
                to the instructions
                <br data-id="element-472" data-mp="3cw1ke8916csg" />
                it requires.
              </h1>
            </div>
            <div className="sub-block" data-id="element-473" data-mp="3cy615qs136a5">
              <p className="sub" style={{
              marginBottom: 16
            }} data-id="element-474" data-mp="3msjtq5t4gxeh">
                An employee asks a question. Synthia identifies the obligations behind it and selects the relevant
                instructions from your library &mdash; drawing on company expertise, regulatory requirements, and
                industry guidance.
              </p>
              <p className="sub" data-id="element-475" data-mp="38umwd76ugmyr">
                Binding requirements go first, ahead of supporting information. The model receives a prepared request.
                A receipt records which instructions and versions were included.
              </p>
              <div className="ctas" data-id="element-476" data-mp="2u2uqrmwuqna8">
                <a className="btn g" href="#process" data-id="element-477" data-mp="2u3w5c1d2trwp">
                  See the process &darr;
                </a>
                <a className="btn o" href={DEMO_URL} target="_blank" rel="noopener" data-id="element-478" data-mp="2q07w7e8sqo9c">
                  Bring your highest standards &rarr;
                </a>
              </div>
            </div>
          </div>
          <div className="col-r" data-id="element-479" data-mp="vlsyynf9k3rs">
            <SeeIt showLink={false} variant="finance" data-id="element-480" data-mp="vkrc7lovxsu8" />
          </div>
        </div>
      </header>

      <div className="stoprow" id="process" style={{
      display: 'block'
    }} data-id="element-481" data-mp="10juitakik74b">
        <span className="clabel" style={{
        display: 'inline-flex',
        marginBottom: 18
      }} data-id="element-482" data-mp="15k9wk38pjgmt">
          The engines
        </span>
        <h2 style={{
        maxWidth: '36ch'
      }} data-id="element-483" data-mp="19kkb44akffda">
          Harvest the rules. Seat the musts.
          <br data-id="element-484" data-mp="cw48p7mcxbir" />
          <span className="g" data-id="element-485" data-mp="cwdzuyj51oq1">Ship the receipt.</span>
        </h2>
      </div>

      <div className="grid3" data-id="element-486" data-mp="htwcp00a0o3b">
        {ENGINES.map(e => <div className="cell" key={e.n} data-id="element-487" data-mp="hs1pff1j8jw2">
            <div className="lab" data-id="element-488" data-mp="lvr9psb6cfba">
              <span data-id="element-489" data-mp="lupzfq1zfwum">{e.stage}</span>
              <span className="n" data-id="element-490" data-mp="qmpx1bukydo1">{e.n}</span>
            </div>
            <h3 data-id="element-491" data-mp="qo0wizgzsz9c">{e.title}</h3>
            <p data-id="element-492" data-mp="3qa7mq3pvxzol">{e.body}</p>
          </div>)}
      </div>

      <div className="split" data-id="element-493" data-mp="3q9ppp4vefhfm">
        <div className="sp-l" data-id="element-494" data-mp="3ubt687wxdc7r">
          <span className="clabel" data-id="element-495" data-mp="3ub1amx3bvehh">Instruction data</span>
          <h2 data-id="element-496" data-mp="3u9gdr0tjjjgg">
            Knowledge describes. <span className="g" data-id="element-497" data-mp="3b6el6sx9thjs">Instructions bind.</span>
          </h2>
          <p className="lede" data-id="element-498" data-mp="3684q0ibjqgy6">
            Your stack has a shelf for every kind of knowledge — warehouses, lakes, vector stores. It has no shelf for
            the rules. So they get pasted into prompts and retrieved only when they happen to{' '}
            <strong data-id="element-499" data-mp="3g5itlhcdfb92">resemble the question</strong>.
          </p>
          <p className="lede" style={{
          marginTop: 14
        }} data-id="element-500" data-mp="3txfkxj67th4e">
            A rule doesn't get retrieved because it's binding. It gets retrieved because it sounds like the query.{' '}
            <strong data-id="element-501" data-mp="3p0yrqak5sckg">Facts get picked; rules get triggered</strong> — and that needs its own asset class: binary
            DO/DON'T atoms with an owner, a version, and a trigger.
          </p>
          <p className="kicker" data-id="element-502" data-mp="2pjsg6a6tdjf">
            Resemblance is a probability.
            <br data-id="element-503" data-mp="29idssbbvefoo" />
            An obligation is not.
          </p>
        </div>
        <div className="sp-r" data-id="element-504" data-mp="24jstce4g001a">
          <ChunkVsAtom data-id="element-505" data-mp="24klv10ojnzck" />
        </div>
      </div>

      <div className="split" data-id="element-506" data-mp="k0gnfjskjz21">
        <div className="sp-l" data-id="element-507" data-mp="k18kz05q86uy">
          <span className="clabel" data-id="element-508" data-mp="k32vdkjdggks">The constitutional tier</span>
          <h2 style={{
          maxWidth: 'none',
          fontSize: 'clamp(24px,2.7vw,34px)'
        }} data-id="element-509" data-mp="oza23fjldw3s">
            A prompt doesn&rsquo;t get
            <br data-id="element-510" data-mp="a8t302ufjia2" />
            <span className="g" data-id="element-511" data-mp="fa09zzor0dfo">to waive your obligations.</span>
          </h2>
          <p className="lede" data-id="element-512" data-mp="f7x9umtvt3x8">
            Whether a request comes from an employee or an agent, some obligations apply regardless of what it asks.
            Task instructions match the work. Standing guidance carries your organization&rsquo;s voice and
            preferences. Constitutional instructions carry the privacy, regulatory, and safety requirements bound to
            your deployment. They must be included on every pass &mdash; even when a request asks to ignore them.
          </p>
          <p className="lede" style={{
          marginTop: 14
        }} data-id="element-513" data-mp="1lnzjz5v7yopr">
            Synthia checks their presence before each model call. A signed receipt records the instructions and
            versions included. Detected attempts to override them are logged for review.
          </p>
          <p className="kicker" data-id="element-514" data-mp="1k83yq3vabe7x">
            If the required instructions can&rsquo;t be included,
            <br data-id="element-515" data-mp="1fallw2e5ceun" />
            the request doesn&rsquo;t run.
          </p>
        </div>
        <div className="sp-r" data-id="element-516" data-mp="1absgyycxdw63">
          <ConstitutionalFlow data-id="element-517" data-mp="1ack5jp83utmv" />
        </div>
      </div>

      <div className="split" data-id="element-518" data-mp="27yr70q9445v0">
        <div className="sp-l" data-id="element-519" data-mp="27xy4y2l3m4j7">
          <span className="clabel" data-id="element-520" data-mp="22zeky7q2f2th">The receipt</span>
          <h2 data-id="element-521" data-mp="230plzzs4lwgw">
            You can't reconstruct a decision <span className="g" data-id="element-522" data-mp="1y8oe0q1zqrwo">you never governed.</span>
          </h2>
          <p className="lede" data-id="element-523" data-mp="1y6v491w2zrkg">
            When the auditor asks why the model said what it said, "we retrieved some relevant passages" is not an
            answer. It isn't even a defense.
          </p>
          <p className="lede" style={{
          marginTop: 14
        }} data-id="element-524" data-mp="2qr26ea4k5lod">
            A receipt is an answer: <strong data-id="element-525" data-mp="2lwplrwrci4b1">obligations fired, versions seated, trigger matched, timestamp signed</strong>
            . Every request ships with its proof — before anyone asks for it.
          </p>
          <p className="kicker" data-id="element-526" data-mp="2y99rgx71f1dr">Before anyone asks.</p>
        </div>
        <div className="sp-r" data-id="element-527" data-mp="3371sqhtknzum">
          <ReceiptCard data-id="element-528" data-mp="336tccfy8qceo" />
        </div>
      </div>

      <div className="split" data-id="element-529" data-mp="386ef71gj74wo">
        <div className="sp-l" data-id="element-530" data-mp="387phsxy8q6m7">
          <span className="clabel" data-id="element-531" data-mp="387zc2xqbizxp">No one between the steps</span>
          <h2 data-id="element-532" data-mp="3d4ppteenhjfy">
            Chat forgives.
            <br data-id="element-533" data-mp="3d3ocf3a5wkv2" />
            <span className="g" data-id="element-534" data-mp="3h7cjlkuvtefs">Agents commit.</span>
          </h2>
          <p className="lede" data-id="element-535" data-mp="3h5je48w0909d">
            In chat, the safety net is a human skimming on a deadline. The prompt carries only what someone thought to
            type, retrieval only fetches what was written down, and review catches the typo but misses the disclosure.
          </p>
          <p className="lede" style={{
          marginTop: 14
        }} data-id="element-536" data-mp="3vxjrj6n9jf28">
            An agent chain removes even that net. Step three's silent omission is step seven's{' '}
            <strong data-id="element-537" data-mp="q0nqp9fpchlo">committed transaction</strong>, and there was no one between the steps to catch it.
          </p>
          <p className="lede" style={{
          marginTop: 14
        }} data-id="element-538" data-mp="l2m80pm6ukaq">
            The obligations have to ride <strong data-id="element-539" data-mp="z2413h6iojpw">inside the payload</strong> — every step, a receipt at every hop.
            Agent governance that lives anywhere else arrives after the commit.
          </p>
        </div>
        <div className="sp-r" data-id="element-540" data-mp="6cnc6ti3vkyf">
          <AgentChain data-id="element-541" data-mp="6dyf6r3q8ooh" />
        </div>
      </div>

      <div className="stoprow" data-id="element-542" data-mp="6aka3b3oi1dc">
        <h2 style={{
        maxWidth: 'none'
      }} data-id="element-543" data-mp="2c4hmrotw0sj">FAQs: How</h2>
      </div>

      <Faq items={faqsHow} data-id="element-544" data-mp="29idz35265fl" />

      <CtaBand data-id="element-545" data-mp="g39ifx3xmz4g" />
      <Ledger data-id="element-546" data-mp="g3roflgecrpk" />
    </>;
}